export const overlay = { 
  selector: '[data-overlay]', 
  reactive: true,

  run(el, data, root) {
    // Create overlay element if it doesn't exist
    if (!document.getElementById('v-overlay')) {
      const overlayEl = document.createElement('div');
      overlayEl.id = 'v-overlay';
      overlayEl.className = 'v-component--overlay';
      document.body.appendChild(overlayEl);
    }

    const overlayEl = document.getElementById('v-overlay');
    const self = this; // Store reference to the component
    
    // Initialize overlay state
    if (!overlayEl.__overlayState) {
      overlayEl.__overlayState = {
        isOpen: false,
        transitionDuration: parseInt(el.getAttribute('data-overlay-duration')) || 300,
        clickOutsideClose: el.getAttribute('data-overlay-outside-close') !== 'false',
        escapeClose: el.getAttribute('data-overlay-escape-close') !== 'false',
        scrollLock: el.getAttribute('data-overlay-scroll-lock') !== 'false',
        target: el.getAttribute('data-overlay-target') || null,
        trigger: el.getAttribute('data-overlay-trigger') || 'click',
        hoverDelay: parseInt(el.getAttribute('data-overlay-hover-delay')) || 200,
        hoverTimeout: null,
        isHovering: false
      };

      // Set initial styles
      overlayEl.style.display = 'none';
      overlayEl.style.position = 'fixed';
      overlayEl.style.top = '0';
      overlayEl.style.left = '0';
      overlayEl.style.width = '100%';
      overlayEl.style.height = '100%';
      overlayEl.style.zIndex = '1000';
      overlayEl.style.transition = `opacity ${overlayEl.__overlayState.transitionDuration}ms ease`;
      overlayEl.style.opacity = '0';
      
      // Handle click outside
      if (overlayEl.__overlayState.clickOutsideClose) {
        overlayEl.addEventListener('click', (e) => {
          if (e.target === overlayEl) {
            self.close(overlayEl);
          }
        });
      }

      // Handle escape key
      if (overlayEl.__overlayState.escapeClose) {
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && overlayEl.__overlayState.isOpen) {
            self.close(overlayEl);
          }
        });
      }
    }

    // Clean up previous event listeners
    if (el._overlayClickHandler) el.removeEventListener('click', el._overlayClickHandler);
    if (el._overlayMouseEnterHandler) el.removeEventListener('mouseenter', el._overlayMouseEnterHandler);
    if (el._overlayMouseLeaveHandler) el.removeEventListener('mouseleave', el._overlayMouseLeaveHandler);
    if (el._overlayFocusHandler) el.removeEventListener('focus', el._overlayFocusHandler);
    if (el._overlayBlurHandler) el.removeEventListener('blur', el._overlayBlurHandler);
    if (overlayEl._overlayMouseEnterHandler) overlayEl.removeEventListener('mouseenter', overlayEl._overlayMouseEnterHandler);
    if (overlayEl._overlayMouseLeaveHandler) overlayEl.removeEventListener('mouseleave', overlayEl._overlayMouseLeaveHandler);

    // Define handlers using arrow functions to maintain 'this' context
    const handleTargetVisibility = () => {
      if (overlayEl.__overlayState.target) {
        const targetEl = document.querySelector(overlayEl.__overlayState.target);
        if (targetEl) {
          targetEl.style.display = overlayEl.__overlayState.isOpen ? 'block' : 'none';
        }
      }
    };

    const handleClick = () => {
      self.toggle(overlayEl);
      handleTargetVisibility();
    };

    const handleMouseEnter = () => {
      overlayEl.__overlayState.isHovering = true;
      clearTimeout(overlayEl.__overlayState.hoverTimeout);
      overlayEl.__overlayState.hoverTimeout = setTimeout(() => {
        if (!overlayEl.__overlayState.isOpen) {
          self.open(overlayEl);
          handleTargetVisibility();
        }
      }, overlayEl.__overlayState.hoverDelay);
    };

    const handleMouseLeave = () => {
      overlayEl.__overlayState.isHovering = false;
      clearTimeout(overlayEl.__overlayState.hoverTimeout);
      overlayEl.__overlayState.hoverTimeout = setTimeout(() => {
        if (!overlayEl.__overlayState.isHovering && overlayEl.__overlayState.isOpen) {
          self.close(overlayEl);
          handleTargetVisibility();
        }
      }, overlayEl.__overlayState.hoverDelay);
    };

    const handleOverlayMouseEnter = () => {
      overlayEl.__overlayState.isHovering = true;
      clearTimeout(overlayEl.__overlayState.hoverTimeout);
    };

    const handleOverlayMouseLeave = () => {
      overlayEl.__overlayState.isHovering = false;
      clearTimeout(overlayEl.__overlayState.hoverTimeout);
      overlayEl.__overlayState.hoverTimeout = setTimeout(() => {
        if (overlayEl.__overlayState.isOpen) {
          self.close(overlayEl);
          handleTargetVisibility();
        }
      }, overlayEl.__overlayState.hoverDelay);
    };

    const handleFocus = () => {
      self.open(overlayEl);
      handleTargetVisibility();
    };

    const handleBlur = () => {
      self.close(overlayEl);
      handleTargetVisibility();
    };

    // Store handlers on elements
    el._overlayClickHandler = handleClick;
    el._overlayMouseEnterHandler = handleMouseEnter;
    el._overlayMouseLeaveHandler = handleMouseLeave;
    el._overlayFocusHandler = handleFocus;
    el._overlayBlurHandler = handleBlur;
    overlayEl._overlayMouseEnterHandler = handleOverlayMouseEnter;
    overlayEl._overlayMouseLeaveHandler = handleOverlayMouseLeave;

    // Set up event listeners based on trigger type
    switch (overlayEl.__overlayState.trigger) {
      case 'hover':
        el.addEventListener('mouseenter', handleMouseEnter);
        el.addEventListener('mouseleave', handleMouseLeave);
        overlayEl.addEventListener('mouseenter', handleOverlayMouseEnter);
        overlayEl.addEventListener('mouseleave', handleOverlayMouseLeave);
        break;
      case 'focus':
        el.addEventListener('focus', handleFocus);
        el.addEventListener('blur', handleBlur);
        break;
      case 'click':
      default:
        el.addEventListener('click', handleClick);
    }

    // Bind to reactive data if specified
    const dataKey = el.getAttribute('data-overlay-bind');
    if (dataKey && data) {
      data.$watch(dataKey, (value) => {
        if (value) {
          self.open(overlayEl);
        } else {
          self.close(overlayEl);
        }
      });
      
      // Set initial state based on data
      if (data[dataKey]) {
        self.open(overlayEl);
      }
    }
  },

  open(el) {
    if (!el.__overlayState || el.__overlayState.isOpen) return;
    
    el.__overlayState.isOpen = true;
    
    // Lock scroll if enabled
    if (el.__overlayState.scrollLock) {
      document.body.style.overflow = 'hidden';
    }
    
    // Show overlay
    el.style.display = 'block';
    
    // Trigger reflow to enable transition
    void el.offsetWidth;
    
    // Start transition
    el.style.opacity = '1';
  },
  
  close(el) {
    if (!el.__overlayState || !el.__overlayState.isOpen) return;
    
    // Clear any pending hover timeouts
    clearTimeout(el.__overlayState.hoverTimeout);
    
    // Start transition
    el.style.opacity = '0';
    
    setTimeout(() => {
      el.style.display = 'none';
      el.__overlayState.isOpen = false;
      
      // Unlock scroll if enabled
      if (el.__overlayState.scrollLock) {
        document.body.style.overflow = '';
      }
      
      // Hide target element if specified
      if (el.__overlayState.target) {
        const targetEl = document.querySelector(el.__overlayState.target);
        if (targetEl) targetEl.style.display = 'none';
      }
    }, el.__overlayState.transitionDuration);
  },
  
  toggle(el) {
    if (!el.__overlayState) return;
    
    if (el.__overlayState.isOpen) {
      this.close(el);
    } else {
      this.open(el);
    }
  }
};